
import { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Loader2,
  Lock,
  LogIn,
  ShieldCheck,
  UserRound,
  Building2,
  KeyRound,
  LayoutGrid,
  FileCheck2,
  ArrowRight,
} from "lucide-react";

import Logo from "../assets/logo2.jpg";

const API_BASE = (
  process.env.REACT_APP_API_URL || "https://supremeadmin.nissi-india.com"
).replace(/\/$/, "");

const API_URL = `${API_BASE}/api/supremeadmin`;

export default function SupremeAdminLogin() {
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (event) => {
    event.preventDefault();
    if (loading) return;

    setError("");

    if (!userName.trim() || !password) {
      setError("Please enter your username and password.");
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.post(
        `${API_URL}/login`,
        {
          username: userName.trim(),
          password,
        },
        {
          withCredentials: true,
          timeout: 15000,
        }
      );

      if (data?.success && data?.user?.role === "SUPREME_ADMIN") {
        const u = data.user;

        // Display-only user information.
        // Actual authentication is stored in an HTTP-only cookie.
        sessionStorage.setItem(
          "infra_v5_supremeadmin",
          JSON.stringify({
            user_id: u.user_id,
            username: u.username,
            role: u.role,
          })
        );

        navigate("/supreme-admin/dashboard", { replace: true });
      } else {
        setError(data?.message || "Login failed.");
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          (err?.code === "ECONNABORTED"
            ? "Connection timed out. Please try again."
            : "Unable to login. Please check your connection.")
      );
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Building2,
      title: "Tenant Management",
      description: "Manage companies and tenant databases",
    },
    {
      icon: KeyRound,
      title: "License Management",
      description: "Manage subscriptions and license validity",
    },
    {
      icon: LayoutGrid,
      title: "Module Management",
      description: "Control access to Infra V5 modules",
    },
    {
      icon: FileCheck2,
      title: "Page Rights",
      description: "Configure page-level permissions",
    },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      {/* Background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(37,99,235,0.18),transparent_55%)]" />
        <div className="absolute -left-32 top-10 h-96 w-96 rounded-full bg-blue-600/10 blur-3xl" />
        <div className="absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-cyan-500/10 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
        <div className="grid w-full overflow-hidden rounded-3xl border border-white/10 bg-slate-900/90 shadow-2xl lg:grid-cols-2">
          {/* LEFT LOGIN SECTION */}
          <div className="flex flex-col justify-center p-7 sm:p-10 lg:p-14">
            <div className="mb-10 flex items-center gap-4">
              <img
                src={Logo}
                alt="Infra V5 Logo"
                className="h-14 w-14 rounded-xl bg-white object-contain p-1.5"
              />

              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-400">
                  Infra V5
                </p>
                <h1 className="text-xl font-bold text-white sm:text-2xl">
                  Supreme Admin
                </h1>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-bold tracking-tight">
                Welcome back
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Sign in to manage your Infra V5 tenants,
                licenses, modules, and page permissions.
              </p>
            </div>

            <div className="mb-7 inline-flex w-fit items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-xs font-medium text-emerald-300">
              <ShieldCheck size={15} />
              Authorized Supreme Admin Access
            </div>

            {error && (
              <div
                role="alert"
                className="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
              >
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label
                  htmlFor="username"
                  className="mb-2 block text-sm font-medium text-slate-300"
                >
                  Username
                </label>

                <div className="relative">
                  <UserRound
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    id="username"
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Enter your username"
                    autoComplete="username"
                    maxLength={100}
                    required
                    disabled={loading}
                    className="w-full rounded-xl border border-slate-700 bg-slate-800/80 py-3.5 pl-12 pr-4 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="mb-2 block text-sm font-medium text-slate-300"
                >
                  Password
                </label>

                <div className="relative">
                  <Lock
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    id="password"
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    required
                    disabled={loading}
                    className="w-full rounded-xl border border-slate-700 bg-slate-800/80 py-3.5 pl-12 pr-12 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60"
                  />

                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-slate-400 transition hover:bg-white/10 hover:text-white"
                    aria-label={showPwd ? "Hide password" : "Show password"}
                  >
                    {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="group flex w-full items-center justify-center gap-3 rounded-xl bg-blue-600 px-5 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn size={18} />
                    Sign In to Supreme Admin
                    <ArrowRight
                      size={17}
                      className="transition group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>

              <div className="flex items-center justify-between gap-3 pt-4 text-xs text-slate-400">
                <Link
                  to="/"
                  className="transition hover:text-blue-400"
                >
                  Back to Home
                </Link>

                <span>
                  © {new Date().getFullYear()} Infra V5
                </span>
              </div>
            </form>
          </div>

          {/* RIGHT INFORMATION SECTION */}
          <div className="relative hidden overflow-hidden border-l border-white/10 bg-gradient-to-br from-blue-950 via-slate-900 to-indigo-950 p-10 lg:flex lg:flex-col lg:justify-center">
            <div className="pointer-events-none absolute -right-28 -top-28 h-80 w-80 rounded-full border border-blue-400/10" />
            <div className="pointer-events-none absolute -right-16 -top-16 h-60 w-60 rounded-full border border-blue-400/10" />

            <div className="relative z-10">
              <div className="mb-8 inline-flex h-16 w-16 items-center justify-center rounded-2xl border border-blue-400/20 bg-blue-500/10">
                <ShieldCheck size={32} className="text-blue-400" />
              </div>

              <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-blue-400">
                Central Administration
              </p>

              <h2 className="max-w-md text-4xl font-bold leading-tight">
                One Platform.
                <br />
                Complete Control.
              </h2>

              <p className="mt-5 max-w-md text-sm leading-7 text-slate-400">
                Manage tenant onboarding, license allocation,
                module access, and page-level rights through
                the Infra V5 Supreme Admin console.
              </p>

              <div className="mt-10 grid grid-cols-1 gap-4">
                {features.map((feature) => {
                  const Icon = feature.icon;

                  return (
                    <div
                      key={feature.title}
                      className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition hover:bg-white/10"
                    >
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400">
                        <Icon size={21} />
                      </div>

                      <div>
                        <h3 className="text-sm font-semibold text-white">
                          {feature.title}
                        </h3>
                        <p className="mt-1 text-xs text-slate-400">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-10 flex items-center gap-2 border-t border-white/10 pt-6 text-xs text-slate-400">
                <Lock size={14} className="text-emerald-400" />
                Restricted administrative portal
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
